import { Controller } from 'react-hook-form'
import { FormControl, FormErrorMessage, FormLabel, VStack } from '@chakra-ui/react'
import { PropsValue, Select } from 'chakra-react-select'

import { useEventFormOptions } from '@hooks/useEventFormOptions'

import {
    CARD_EVENT_TYPES,
    MatchEventEditorEventType,
    PENALTY_MISSED_EVENT_TYPES,
    TMatchEventEditorEventType,
} from '@common/types'

import { useMatchEditorFormContext } from '@contexts/MatchEditorFormContext'

import { hasEventReasonFields, Option } from '../utils'
import { EnumRadioGroup } from './EnumRadioGroup'

export function EventReasonFields() {
    const { zodForm, isFormDisabled } = useMatchEditorFormContext()
    const { cardLocation, cardReason, penaltyMissedReason, substitutionReason } = useEventFormOptions()

    const eventType = Number(zodForm.watch('type.value')) as TMatchEventEditorEventType

    if (!hasEventReasonFields(eventType)) {
        return null
    }

    const isCardEvent = CARD_EVENT_TYPES.has(eventType)
    const isPenaltyMissedEvent = PENALTY_MISSED_EVENT_TYPES.has(eventType)
    const isSubstitutionEvent = eventType === MatchEventEditorEventType.substitution

    return (
        <VStack alignItems="flex-start" w="100%" spacing={4} mt={4}>
            {isCardEvent && (
                <Controller
                    control={zodForm.control}
                    name="card_location"
                    render={({ field: { onChange, onBlur, value, name, ref } }) => (
                        <EnumRadioGroup
                            name={name}
                            label="Card location"
                            options={cardLocation}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            isInvalid={!!zodForm.formState.errors.card_location}
                            isDisabled={isFormDisabled}
                            errorMessage={zodForm.formState.errors.card_location?.message}
                        />
                    )}
                />
            )}

            {isCardEvent && (
                <Controller
                    control={zodForm.control}
                    name="card_reason"
                    render={({ field: { onChange, onBlur, value, name, ref } }) => (
                        <FormControl isInvalid={!!zodForm.formState.errors.card_reason} isDisabled={isFormDisabled}>
                            <FormLabel htmlFor={name}>Card reason</FormLabel>
                            <Select
                                id={name}
                                isSearchable
                                isClearable
                                name={name}
                                ref={ref}
                                onChange={onChange}
                                onBlur={onBlur}
                                value={value as unknown as PropsValue<Option>}
                                placeholder="Select card reason"
                                options={cardReason}
                            />
                            <FormErrorMessage>{zodForm.formState.errors.card_reason?.message}</FormErrorMessage>
                        </FormControl>
                    )}
                />
            )}

            {isPenaltyMissedEvent && (
                <Controller
                    control={zodForm.control}
                    name="penalty_missed_reason"
                    render={({ field: { onChange, onBlur, value, name, ref } }) => (
                        <EnumRadioGroup
                            name={name}
                            label="Penalty missed reason"
                            options={penaltyMissedReason}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            isInvalid={!!zodForm.formState.errors.penalty_missed_reason}
                            isDisabled={isFormDisabled}
                            errorMessage={zodForm.formState.errors.penalty_missed_reason?.message}
                        />
                    )}
                />
            )}

            {isSubstitutionEvent && (
                <Controller
                    control={zodForm.control}
                    name="substitution_reason"
                    render={({ field: { onChange, onBlur, value, name, ref } }) => (
                        <EnumRadioGroup
                            name={name}
                            label="Substitution reason"
                            options={substitutionReason}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            ref={ref}
                            isInvalid={!!zodForm.formState.errors.substitution_reason}
                            isDisabled={isFormDisabled}
                            errorMessage={zodForm.formState.errors.substitution_reason?.message}
                        />
                    )}
                />
            )}
        </VStack>
    )
}
